import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Divider, Empty, List, Modal, Select, Space, Typography, message } from 'antd';
import { errorMessage, space } from '@viq-admin/ui';
import { crmOwners } from '../../api/crmUpload';
import { saveCrmOwnerDefaults } from '../../api/crmOwnerDefaults';
import type { Conference, CrmConnection } from '../../api/types';

const { Text } = Typography;

export interface OwnerDefaultsModalProps {
  open: boolean;
  onClose: () => void;
  /** e.g. 'salesforce' — the URL segment, matching `connection.crmSource`. */
  source: string;
  /** Display label for the title/copy, e.g. 'Salesforce'. */
  sourceLabel: string;
  connection: CrmConnection;
  conferences: Conference[];
  /** Fired after every successful save — the caller invalidates `crmConnections` so `connection` refreshes. */
  onSaved: () => void;
}

/** `{name} — {role}`, matching the label the Match/Send owner picker uses. */
function ownerLabel(owner: { name: string; role: string }): string {
  return `${owner.name} — ${owner.role}`;
}

/**
 * Owner-defaults settings for one connected CRM (Task 7). Two independent
 * settings, each with its own immediate-save action rather than a single
 * form-wide Save — this mirrors the rest of the page (Test/Disconnect fire
 * on click, no draft state to lose track of) and keeps "clear" and "set"
 * unambiguous:
 *
 *  - Company default owner: a Select over the role-filtered owners list,
 *    plus a Save button. "Clear" is a SEPARATE button, deliberately not
 *    routed through the Select's own value — the backend only needs the
 *    live owners list to validate a SET, never a clear (see
 *    `crmOwnerDefaults.ts`'s docblock), so Clear stays enabled even when
 *    the owners fetch below has failed. Save stays disabled in that case
 *    (nothing to validate a pick against), which also means an owners-list
 *    failure never blocks a tenant from removing a stale default.
 *  - Per-conference overrides: existing entries in
 *    `connection.config.defaultOwnerByConference` render as a plain list
 *    with a Remove button (same "always enabled" reasoning as Clear above).
 *    A single "Add override" mini-form (conference + owner, both required)
 *    appends or replaces one entry — editing an existing override is
 *    Remove-then-re-add rather than inline editing, which keeps this to one
 *    owner Select on screen at a time instead of one per row.
 *
 * Every write sends only the field(s) it touches — never the row's whole
 * `config` — so `mergePolicy`/`ownerRoleFilter`/provider fields are never at
 * risk from this screen, by construction rather than by convention.
 */
export function OwnerDefaultsModal({
  open,
  onClose,
  source,
  sourceLabel,
  connection,
  conferences,
  onSaved,
}: OwnerDefaultsModalProps) {
  const ownersQuery = useQuery({
    queryKey: ['crmOwners', source],
    queryFn: () => crmOwners(source),
    enabled: open,
  });
  // `ownersQuery.data` itself is referentially stable across renders that
  // don't refetch (react-query memoizes it) — `owners` only needs its OWN
  // memo so the `?? []` fallback doesn't hand every downstream `useMemo` a
  // fresh array identity on every render that leaves `ownersQuery.data`
  // unchanged.
  const owners = useMemo(() => ownersQuery.data ?? [], [ownersQuery.data]);
  const ownersUnavailable = ownersQuery.isError;
  const ownerOptions = useMemo(
    () => owners.map((o) => ({ value: o.id, label: ownerLabel(o) })),
    [owners],
  );

  const [companyOwnerId, setCompanyOwnerId] = useState<string | undefined>(undefined);
  const [companySaving, setCompanySaving] = useState<'save' | 'clear' | null>(null);

  const [newConferenceId, setNewConferenceId] = useState<string | undefined>(undefined);
  const [newOwnerId, setNewOwnerId] = useState<string | undefined>(undefined);
  const [addSaving, setAddSaving] = useState(false);
  const [removingConferenceId, setRemovingConferenceId] = useState<string | null>(null);

  const currentDefaultOwnerId = connection.config.defaultOwnerId;
  // Same reasoning as `owners` above — memoized so its own `?? {}` fallback
  // doesn't churn `overrides`/`addableConferences` below on every render.
  const overrideMap = useMemo(
    () => connection.config.defaultOwnerByConference ?? {},
    [connection.config.defaultOwnerByConference],
  );
  const overrides = useMemo(
    () =>
      Object.entries(overrideMap).map(([conferenceId, ownerId]) => ({
        conferenceId,
        ownerId,
        conferenceName: conferences.find((c) => c.id === conferenceId)?.name ?? conferenceId,
        ownerName: owners.find((o) => o.id === ownerId)?.name ?? ownerId,
      })),
    [overrideMap, conferences, owners],
  );
  // Conferences not already overridden — the add form only ever creates a
  // NEW entry; changing an existing one is Remove-then-re-add (see docblock).
  const addableConferences = useMemo(
    () => conferences.filter((c) => !(c.id in overrideMap)),
    [conferences, overrideMap],
  );

  async function handleSaveCompanyDefault() {
    if (!companyOwnerId) return;
    setCompanySaving('save');
    try {
      await saveCrmOwnerDefaults(source, { defaultOwnerId: companyOwnerId });
      message.success('Company default owner saved.');
      setCompanyOwnerId(undefined);
      onSaved();
    } catch (err: unknown) {
      message.error(errorMessage(err, 'Failed to save the company default owner.'));
    } finally {
      setCompanySaving(null);
    }
  }

  async function handleClearCompanyDefault() {
    setCompanySaving('clear');
    try {
      await saveCrmOwnerDefaults(source, { defaultOwnerId: null });
      message.success('Company default owner cleared.');
      setCompanyOwnerId(undefined);
      onSaved();
    } catch (err: unknown) {
      message.error(errorMessage(err, 'Failed to clear the company default owner.'));
    } finally {
      setCompanySaving(null);
    }
  }

  async function handleAddOverride() {
    if (!newConferenceId || !newOwnerId) return;
    setAddSaving(true);
    try {
      await saveCrmOwnerDefaults(source, {
        conferenceDefault: { conferenceId: newConferenceId, ownerId: newOwnerId },
      });
      message.success('Conference default owner saved.');
      setNewConferenceId(undefined);
      setNewOwnerId(undefined);
      onSaved();
    } catch (err: unknown) {
      message.error(errorMessage(err, 'Failed to save the conference default owner.'));
    } finally {
      setAddSaving(false);
    }
  }

  async function handleRemoveOverride(conferenceId: string) {
    setRemovingConferenceId(conferenceId);
    try {
      await saveCrmOwnerDefaults(source, {
        conferenceDefault: { conferenceId, ownerId: null },
      });
      message.success('Conference default owner removed.');
      onSaved();
    } catch (err: unknown) {
      message.error(errorMessage(err, 'Failed to remove the conference default owner.'));
    } finally {
      setRemovingConferenceId(null);
    }
  }

  return (
    <Modal
      title={`${sourceLabel} owner defaults`}
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
      destroyOnClose
      width={560}
    >
      <Space direction="vertical" size={space.lg} style={{ width: '100%' }}>
        {ownersUnavailable && (
          <Alert
            type="warning"
            showIcon
            message="Couldn’t load the owners list."
            description="Setting a new default owner isn’t available right now, but clearing an existing one still works."
          />
        )}

        <div>
          <Typography.Title level={5}>Company default owner</Typography.Title>
          <Text type="secondary">
            Used for leads at any conference without its own default owner below.
          </Text>
          <div style={{ marginTop: space.sm }}>
            <Space wrap>
              <Select
                aria-label="Company default owner"
                showSearch
                allowClear
                placeholder={
                  currentDefaultOwnerId
                    ? (owners.find((o) => o.id === currentDefaultOwnerId)?.name ??
                      currentDefaultOwnerId)
                    : 'No default set'
                }
                style={{ minWidth: 240 }}
                value={companyOwnerId}
                disabled={ownersUnavailable}
                options={ownerOptions}
                filterOption={(input, option) =>
                  (option?.label as string | undefined)?.toLowerCase().includes(input.toLowerCase()) ??
                  false
                }
                onChange={(value: string | undefined) => setCompanyOwnerId(value)}
              />
              <Button
                type="primary"
                disabled={!companyOwnerId}
                loading={companySaving === 'save'}
                onClick={handleSaveCompanyDefault}
              >
                Save default owner
              </Button>
              <Button
                disabled={!currentDefaultOwnerId}
                loading={companySaving === 'clear'}
                onClick={handleClearCompanyDefault}
              >
                Clear
              </Button>
            </Space>
          </div>
        </div>

        <Divider style={{ margin: 0 }} />

        <div>
          <Typography.Title level={5}>Per-conference default owners</Typography.Title>
          <Text type="secondary">Overrides the company default for leads at that conference.</Text>
          <div style={{ marginTop: space.sm }}>
            {overrides.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No conference overrides yet"
              />
            ) : (
              <List
                size="small"
                dataSource={overrides}
                renderItem={(row) => (
                  <List.Item
                    actions={[
                      <Button
                        key="remove"
                        danger
                        size="small"
                        aria-label={`Remove override for ${row.conferenceName}`}
                        loading={removingConferenceId === row.conferenceId}
                        onClick={() => handleRemoveOverride(row.conferenceId)}
                      >
                        Remove
                      </Button>,
                    ]}
                  >
                    <Text>
                      {row.conferenceName}: <strong>{row.ownerName}</strong>
                    </Text>
                  </List.Item>
                )}
              />
            )}
          </div>

          <div style={{ marginTop: space.md }}>
            <Text strong>Add override</Text>
            <div style={{ marginTop: space.sm }}>
              <Space wrap>
                <Select
                  aria-label="Conference"
                  showSearch
                  placeholder="Conference"
                  style={{ minWidth: 200 }}
                  value={newConferenceId}
                  options={addableConferences.map((c) => ({ value: c.id, label: c.name }))}
                  filterOption={(input, option) =>
                    (option?.label as string | undefined)
                      ?.toLowerCase()
                      .includes(input.toLowerCase()) ?? false
                  }
                  onChange={(value: string | undefined) => setNewConferenceId(value)}
                />
                <Select
                  aria-label="Owner"
                  showSearch
                  placeholder="Owner"
                  style={{ minWidth: 200 }}
                  value={newOwnerId}
                  disabled={ownersUnavailable}
                  options={ownerOptions}
                  filterOption={(input, option) =>
                    (option?.label as string | undefined)
                      ?.toLowerCase()
                      .includes(input.toLowerCase()) ?? false
                  }
                  onChange={(value: string | undefined) => setNewOwnerId(value)}
                />
                <Button
                  type="primary"
                  disabled={!newConferenceId || !newOwnerId}
                  loading={addSaving}
                  onClick={handleAddOverride}
                >
                  Save conference default
                </Button>
              </Space>
            </div>
          </div>
        </div>
      </Space>
    </Modal>
  );
}
